import { FormikProps } from "formik"
import { forwardRef, Suspense, useRef } from "react"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"

import { CreateUpdateDeviceFormNew } from "../components/CreateUpdateDeviceFormNew"
import { CreateDisplayFormValues, DisplayTypes } from "../deviceTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { updateRoomDisplayDevice } from "../../../../../serviceCenter/report/home/serviceCenterHomeApi"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { useNavigate, useParams } from "react-router-dom"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    DeviceDetailsQuery,
    DeviceDetailsResponse,
    fetchDeviceDetails,
} from "../../../../../entities/devices/deviceApi"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
} from "../../../../../../constants/routes"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"

import { ServiceCenterDevicesQuery } from "../../../../../serviceCenter/admin/organization/devices/serviceCenterDevicesApi"

export function EditDevice() {
    const roomFormRef = useRef<FormikProps<CreateDisplayFormValues>>(null)

    const handleSaveClick = () => {
        roomFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="service-center-admin-organization-edit-devices-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_EDIT_ROUTE}
                    title="Devices"
                    icon={<FontAwesomeIcon icon={faShieldCheck} />}
                    rightElement={
                        <LocalizedStrict id="devices-edit-device-form">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization-devices">
                            <Breadcrumb>Devices</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization-devices-edit">
                            <Breadcrumb>Edit display</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Device ref={roomFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const transformData = (data: DeviceDetailsResponse): CreateDisplayFormValues => {
    const { isActive, resource, type, name, serialNumber, deviceNumber, template } = data.device

    return {
        isActive,
        type: type as DisplayTypes,
        name,
        serialNumber,
        deviceNumber,
        room: resource,
        templateId: template?.id,
    }
}

const Device = forwardRef<FormikProps<CreateDisplayFormValues>>((_props, ref) => {
    const navigate = useNavigate()
    const { deviceId } = useParams()
    const deviceIdNumber = Number(deviceId)

    const roomDetails = useApiQuery(fetchDeviceDetails, {
        queryName: DeviceDetailsQuery.DETAILS,
        dependencies: {
            deviceId: deviceIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(deviceId),
        select: transformData,
    })

    const { mutateAsync: onDeviceCreate } = useApiMutation(updateRoomDisplayDevice, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ServiceCenterDevicesQuery.DEVICES] })
            navigate(SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_ROUTE)
        },
    })

    return (
        <CreateUpdateDeviceFormNew
            ref={ref}
            isEditMode
            initialValues={roomDetails.data}
            onSubmit={(body) => {
                onDeviceCreate({
                    deviceId: deviceIdNumber,
                    ...body,
                })
            }}
        />
    )
})
